// ADR 0023 Sprint 7 Wave B — Platform tenant context.
//
// Provides a session-scoped "currently selected tenant" across all
// platform management screens. Selection is SESSION state only (useState).
//
// Security notes:
//   - NOT stored in localStorage (would persist across sessions and risk
//     data leakage if operators share devices or leave sessions open).
//   - NOT stored in URL params (tenant IDs in URLs could be cached by
//     intermediaries or leaked in referrer headers).
//   - The selected tenant is a UI convenience only. Every API call still
//     sends the target-tenant-id header; the backend derives the authorised
//     tenant from the JWT — the UI selection does NOT override server-side
//     access control.
//
// Phase 3 (tenant-view switcher):
//   - PlatformTenantProvider is now mounted at AppShell level (wraps all
//     authenticated routes) rather than only in /platform/* and
//     /admin/scenarios* layouts. The route-scoped providers became nested
//     no-ops. The promotion means every operational feature (alerts, cases,
//     customers, etc.) has access to the context via usePlatformTenantOptional.
//   - On every state change the provider writes the selected tenant id into
//     the module-level tenant-view-store so the HTTP client can attach the
//     target-tenant-id header without a React hook.
import { createContext, useContext, useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { setActiveTenantId } from '@/api/tenant-view-store'
import { writeTenantViewMarker, clearTenantViewMarker } from './tenant-view-marker'
import type { TenantSummary } from './types'

// ---- Context shape -----------------------------------------------------------

interface PlatformTenantContextValue {
  /** The public_id of the currently selected tenant, or null if none. */
  selectedTenantId: string | null
  /** The full summary object for the selected tenant, or null if none. */
  selectedTenant: TenantSummary | null
  /** Set the selected tenant (pass null to deselect). */
  setSelectedTenant: (tenant: TenantSummary | null) => void
}

const PlatformTenantContext = createContext<PlatformTenantContextValue | null>(null)

// ---- Provider ----------------------------------------------------------------

interface PlatformTenantProviderProps {
  children: ReactNode
}

export function PlatformTenantProvider({ children }: PlatformTenantProviderProps) {
  const [selectedTenant, setSelectedTenantState] = useState<TenantSummary | null>(null)
  const queryClient = useQueryClient()
  // Track the previous tenant public_id so we only invalidate on an actual change.
  const prevTenantIdRef = useRef<string | null>(null)

  // Security fix W1: update the module-level store SYNCHRONOUSLY inside the
  // setter so that any query dispatched in the SAME render cycle as a tenant
  // switch already carries the correct tenant id. The previous implementation
  // called setActiveTenantId inside useEffect (post-paint), which meant the
  // first query fired after a state update could read the stale tenant id and
  // receive a backend 403 before the effect had a chance to run.
  //
  // The backend will reject the request with 403 (no data leak), but the
  // spurious error is visible to operators and pollutes Sentry. Writing
  // synchronously here eliminates the race entirely.
  const setSelectedTenant = (tenant: TenantSummary | null) => {
    setActiveTenantId(tenant?.public_id ?? null)
    setSelectedTenantState(tenant)
    // Accidental-drop breadcrumb (see tenant-view-marker.ts): every real
    // entry/switch refreshes the marker; the ONLY caller that passes
    // `null` is an explicit "Exit tenant view" click (TenantSelectorBand),
    // so clearing here is exactly the "deliberate exit" signal that
    // TenantViewDroppedNotice relies on to stay silent. A hard navigation
    // or reload never runs this line, so the marker survives and the
    // notice fires on the next mount instead.
    if (tenant) {
      writeTenantViewMarker({ public_id: tenant.public_id, name: tenant.name, code: tenant.code })
    } else {
      clearTenantViewMarker()
    }
  }

  // Handles query-cache invalidation on tenant change only.
  // setActiveTenantId is intentionally NOT called here — it is updated
  // synchronously in the setter above. The unmount cleanup is handled by
  // a separate useEffect with empty deps below to avoid the effect-re-run
  // cleanup sequence wiping the store value that the setter just wrote.
  //
  // Adversarial fix A2 (stale-cache across tenant switches):
  //   When a platform admin switches from one tenant to another (or exits
  //   tenant-view mode), all operational query keys (alerts, cases, customers,
  //   transactions, screening, etc.) still hold data from the PREVIOUS tenant
  //   context. The backend correctly rejects/rescopes the new requests, but
  //   until the queries refetch, stale data from the wrong tenant would be
  //   briefly visible in the UI.
  //
  //   Fix: invalidate ALL queries (except platform-management ones — they
  //   scoped by explicit targetTenantId in their query keys) whenever the
  //   selected tenant changes. We skip the initial mount (prevTenantIdRef null →
  //   undefined/null) to avoid flushing the cache on normal page load for
  //   non-platform-admin users who never trigger this.
  useEffect(() => {
    const newId = selectedTenant?.public_id ?? null
    const prevId = prevTenantIdRef.current
    prevTenantIdRef.current = newId

    // Only invalidate when the tenant ID actually changes from a previously set
    // value (including null → tenantId for the first selection, or tenantId → null
    // for exit, or tenantId → differentTenantId for a switch). Skip the very first
    // mount (prevId === null AND newId === null) so normal users don't pay the cost.
    if (prevId !== newId && !(prevId === null && newId === null)) {
      // Invalidate operational query keys. We invalidate the root so any
      // descendant key (e.g. ['cases', caseId]) is also invalidated.
      // Platform-management keys (['platform', ...]) are intentionally NOT
      // included — they scope by explicit targetTenantId and are unaffected.
      const operationalRoots = [
        ['alerts'],
        ['cases'],
        ['customers'],
        ['transactions'],
        ['screening'],
        ['adverse-media'],
        ['blocks'],
        ['sars'],
        ['reports'],
        ['analytics'],
        ['audit'],
        ['notifications'],
        ['inbox'],
        ['handovers'],
        ['lists'],
        ['rules'],
        ['scenarios'],
        ['operations'],
        ['branches'],
      ]
      for (const key of operationalRoots) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    }
  }, [selectedTenant, queryClient])

  // Separate unmount-only cleanup effect: clear the store when the provider
  // is destroyed (e.g. user logs out and the auth shell unmounts). This is
  // intentionally separate from the invalidation effect above so its cleanup
  // only fires on true unmount — NOT on every dependency re-run (which would
  // wipe the synchronous store value the setter just wrote before the
  // invalidation effect's new body runs).
  //
  // Also clears the accidental-drop breadcrumb (tenant-view-marker.ts): a
  // true SPA-level unmount happens on the SPA /login navigate (see
  // useLogout in features/auth/hooks.ts) and on an accidental hard
  // navigation/reload — for the latter, the JS runtime tears down before
  // this cleanup gets a chance to run, so the breadcrumb correctly
  // survives THAT case for TenantViewDroppedNotice to pick up on the next
  // mount. A DELIBERATE logout is different: there's no "next mount" in
  // the current tab to notify, so the breadcrumb must not survive it.
  //
  // Adversarial fix C1: this effect alone is NOT sufficient for that,
  // because the OIDC logout branch (`window.location.href =
  // '/api/v1/auth/oidc/end-session'`, see useLogout) is itself a hard
  // navigation — React never gets to run this cleanup before the JS
  // runtime tears down, exactly like an accidental drop. Relying on this
  // effect alone would leak admin A's tenant-view breadcrumb across the
  // OIDC round-trip to whoever signs in next in the same tab. The fix is
  // in features/auth/api.ts's `logout()`, which clears the marker
  // directly (symmetric with clearTokens()) BEFORE either post-logout
  // navigation branch runs — independent of component unmount timing.
  // The clear here is now belt-and-braces (covers the SPA-navigate case
  // as well, redundantly but harmlessly).
  useEffect(() => {
    return () => {
      setActiveTenantId(null)
      clearTenantViewMarker()
    }
  }, [])

  return (
    <PlatformTenantContext.Provider
      value={{
        selectedTenantId: selectedTenant?.public_id ?? null,
        selectedTenant,
        setSelectedTenant,
      }}
    >
      {children}
    </PlatformTenantContext.Provider>
  )
}

// ---- Hook --------------------------------------------------------------------

/**
 * Access the platform tenant context. Must be used inside a
 * <PlatformTenantProvider>.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the provider, hook, and header helper are the same surface; splitting them would force every consumer through a barrel import
export function usePlatformTenant(): PlatformTenantContextValue {
  const ctx = useContext(PlatformTenantContext)
  if (ctx === null) {
    throw new Error('usePlatformTenant must be used inside <PlatformTenantProvider>')
  }
  return ctx
}

/**
 * Like usePlatformTenant but returns null when no provider is in scope.
 * Use this in components that may render both inside and outside
 * PlatformTenantProvider (e.g. scenario detail page used from both
 * /admin/scenarios and tenant-admin surfaces).
 */
// eslint-disable-next-line react-refresh/only-export-components -- same reasoning as usePlatformTenant above
export function usePlatformTenantOptional(): PlatformTenantContextValue | null {
  return useContext(PlatformTenantContext)
}

// ---- Header helper -----------------------------------------------------------

/**
 * Returns a headers object with the target-tenant-id header when an id is
 * provided, or an empty object when id is absent/null/undefined.
 *
 * Platform API modules merge this into their fetch options so every call
 * that is tenant-scoped includes the correct header.
 *
 * Security: The backend validates the target-tenant-id header against the
 * caller's JWT. This helper is a convenience only — it does not bypass
 * any server-side check.
 */
// eslint-disable-next-line react-refresh/only-export-components -- co-located with context; this helper is always used alongside the context exports
export function targetTenantHeader(id?: string | null): Record<string, string> {
  if (!id) return {}
  return { 'target-tenant-id': id }
}
